import { type ScenarioPassCriteria } from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import { type TargetScopeReferenceScenarioTypes } from 'components/scenarios/recommended-detail/utils/configure-scenario'
import { type graphNodeToStepAttack } from 'components/scenarios/utils/graph/legacy-graph'
import {
  type EndpointConfiguration,
  type EndpointResponse,
  type ResponseBodyEvaluation,
  type StatusCheckCategory
} from 'types/api/status-checks/status-check'

import type { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import type { CamelCasedProperties, SnakeCasedProperties } from 'type-fest'
import type {
  AttackCommand,
  AttackConfiguration,
  SnakeCaseAttackArgs,
  TargetingStrategy
} from 'types/api/attacks/details/targeting'
import type {
  ExperimentDuration,
  ExperimentEffect,
  FailureFlagSelector
} from 'types/api/failure-flags/failure-flags'
import type { K8sObject } from 'types/api/kubernetes/kubernetes'
import type { ScenarioRun } from 'types/api/scenarios/runs/scenario-runs'
import type { EvaluationResults } from 'types/api/status-checks/evaluation'
import type { PagedResponse } from 'types/paged-response'

export enum ScenarioGraphNodeType {
  InfraAttack = 'InfraAttack',
  FailureFlag = 'FailureFlag',
  ExternalAction = 'ExternalAction',
  RollbackExternalAction = 'RollbackExternalAction',
  ContinuousStatusCheck = 'ContinuousStatusCheck',
  SynchronousStatusCheck = 'SynchronousStatusCheck',
  Delay = 'Delay',
  Concurrent = 'Concurrent',
  Continuous = 'Continuous' // forbidden in production
}

export enum ScenarioLifecycle {
  NotStarted = 'NotStarted',
  Active = 'Active',
  HaltRequested = 'HaltRequested',
  Successful = 'Successful',
  Halted = 'Halted',
  Failed = 'Failed',
  UserHalted = 'UserHalted'
}

export enum SentinelFlags {
  RANDOM_DNS_SERVER = 'RANDOM_DNS_SERVER_SENTINEL',
  RANDOM_ZONE = 'RANDOM_ZONE_SENTINEL',
  FAILURE_FLAG = 'FAILURE_FLAG_SENTINEL',
  DEPENDENCY_ID = 'DEPENDENCY_ID_SENTINEL'
}

export enum ExternalActionRequestType {
  DisableAWSAutoScalingGroups = 'DisableAWSAutoScalingGroups',
  RemoveAWSSecurityGroups = 'RemoveAWSSecurityGroups'
}

export interface BaseNodeState {
  lifecycle: ScenarioLifecycle
  startTime?: string // Date
  endTime?: string // Date
  failedReason?: string
  nodeHaltedByGuid?: string
  nodeHaltedById?: string
}

export interface InfraAttackNodeState extends BaseNodeState {
  k8sAttackId?: string
  taskId: string
  taskSummary: {
    clientsMetricsEnabledCount: number
    endTime: string
    stage: string
    totalClients: number
  }
  resolvedTargets: {
    resolvedHosts: string[]
    resolvedContainers: Record<string, string>[]
  }
}

export interface FailureFlagNodeState
  extends SnakeCasedProperties<BaseNodeState> {
  experiment_id: string
  run_summary?: {
    deliveries: number
    impacts: number
    start_time: string
    stop_time: string
  }
}

export interface ExternalActionNodeState extends BaseNodeState {
  errorCode?: string
  hasStateBeenModified?: boolean
  requestId?: string
  statusCode?: number
  warningMessage?: string
}

export interface StatusCheckNodeState extends BaseNodeState {
  privateNetworkInvocationCompleted?: boolean // defaults to false
  privateNetworkInvocationId?: string
  notificationSent?: boolean
  endpointResponse: EndpointResponse
  evaluationResults: EvaluationResults
  lastExecutedAt?: Date
}

export type CamelCaseNodeState = InfraAttackNodeState | StatusCheckNodeState
export type SnakeCaseNodeState = SnakeCasedProperties<CamelCaseNodeState>

export type NodeState = CamelCaseNodeState & SnakeCaseNodeState

export interface BaseAWSExternalActionRequest {
  resourceArn: string
}

export interface AWSAutoScalingGroupExternalActionRequest
  extends BaseAWSExternalActionRequest {
  type: ExternalActionRequestType.DisableAWSAutoScalingGroups
  processesToDisable?: string[]
}

export interface AWSSecurityGroupsExternalActionRequest
  extends BaseAWSExternalActionRequest {
  type: ExternalActionRequestType.RemoveAWSSecurityGroups
  securityGroupIds: string[]
  zone: string
}

export type ExternalActionRequest =
  | AWSAutoScalingGroupExternalActionRequest
  | AWSSecurityGroupsExternalActionRequest

export interface BaseGraphNode {
  id?: string
  next?: string
  guid: string
  type?: ScenarioGraphNodeType
}

export interface InfraAttackNode extends BaseGraphNode {
  type: ScenarioGraphNodeType.InfraAttack
  target_definition?: TargetDefinition
  impact_definition?: ImpactDefinition
  attack_configuration: AttackConfiguration
  include_new_targets?: boolean
  continue_on_empty_targets?: boolean
  state?: SnakeCasedProperties<InfraAttackNodeState>
}

export interface FailureFlagInputForScenario {
  app: FailureFlagSelector
  duration: ExperimentDuration
  effect: ExperimentEffect
  flag: FailureFlagSelector
  rate: number
  staticTargeting: boolean
}

export interface FailureFlagNode extends BaseGraphNode {
  failureFlagInputForScenario: FailureFlagInputForScenario
  state?: FailureFlagNodeState
  type: ScenarioGraphNodeType.FailureFlag
}

export interface BaseExternalActionNode extends BaseGraphNode {
  externalActionRequest: ExternalActionRequest
  state: SnakeCasedProperties<ExternalActionNodeState>
}

export interface ExternalActionNode extends BaseExternalActionNode {
  type: ScenarioGraphNodeType.ExternalAction
}

export interface RollbackExternalActionNode extends BaseExternalActionNode {
  referenceExternalActionId?: string // binds the rollback node to a single external action node
  type: ScenarioGraphNodeType.RollbackExternalAction
}

export interface StatusCheckNode extends BaseGraphNode {
  autoGeneratedStatusCheckId?: string
  category?: StatusCheckCategory | null
  description?: string
  endpointConfiguration: EndpointConfiguration
  evaluationConfiguration: EvaluationConfiguration
  externalIntegrationId?: string
  isAutoGenerated?: boolean
  isPrivateNetwork?: boolean
  name: string
  payload?: string
  pollingIntervalSeconds: number
  rawEndpointConfiguration?: EndpointConfiguration
  state?: SnakeCasedProperties<StatusCheckNodeState>
  statusCheckId?: string | null // null if the status check was deleted
  statusCheckReferenceId?: string | null // null if the status check was deleted
  thirdPartyPresets?: string
}

export interface ContinuousStatusCheckNode extends StatusCheckNode {
  type: ScenarioGraphNodeType.ContinuousStatusCheck
  intervalInSeconds?: number
}

export interface SynchronousStatusCheckNode extends StatusCheckNode {
  type: ScenarioGraphNodeType.SynchronousStatusCheck
  isHaltable?: boolean
}

export interface StatusCheckReference {
  type: string
  statusCheckId: string
  guid?: string
  next: string | null
}

export interface ConcurrentNode extends BaseGraphNode {
  branches: ScenarioGraph[]
  type: ScenarioGraphNodeType.Concurrent
  state?: SnakeCasedProperties<BaseNodeState>
}

export interface DelayNode extends BaseGraphNode {
  delay: number
  type: ScenarioGraphNodeType.Delay
  state?: SnakeCasedProperties<BaseNodeState>
}

export type GraphNode =
  | InfraAttackNode
  | FailureFlagNode
  | ExternalActionNode
  | RollbackExternalActionNode
  | DelayNode
  | ConcurrentNode
  | SynchronousStatusCheckNode
  | ContinuousStatusCheckNode

export interface GraphNodeList {
  [key: string]: GraphNode
}

export interface BaseGraph {
  observers?: ContinuousStatusCheckNode[]
  nodes: GraphNodeList
  external_actions?: ExternalActionNode[]
  external_rollbacks?: RollbackExternalActionNode[]
  start_id: string
  expected_length?: number
  expected_length_with_delays?: number
}

export interface ScenarioGraph
  extends BaseGraph,
    CamelCasedProperties<BaseGraph> {}

export interface MultiSelect {
  [key: string]: string[]
}

export interface EvaluationConfiguration {
  okLatencyMaxMs: number
  okStatusCodes: string[]
  responseBodyEvaluation: ResponseBodyEvaluation | null
}

export interface ImpactDefinition {
  infra_command_type: AttackCommand
  infra_command_args: SnakeCaseAttackArgs
}

export interface TargetDefinition {
  containerSelection?: {
    selectionType: string
    containerNames?: string[]
  }
  k8s_objects?: K8sObject[]
  strategy_type: string
  strategy: Strategy
  target_type: ProvenanceTargetType
}

export interface Strategy {
  all_hosts?: boolean
  all_containers?: boolean
  attrs?: Attrs
  count?: number
  percentage?: number
  type: string
}

export interface Attrs {
  multiSelectLabels?: MultiSelect
  multiSelectTags?: MultiSelect
}

export interface StepAttack {
  attackType: 'ILFI'
  attackConfiguration: AttackConfiguration
  targetDefinition: TargetDefinition
  impactDefinition: ImpactDefinition
  includeNewTargets?: boolean
  include_new_clients?: boolean // legacy
  continueOnEmptyTargets?: boolean
}

export interface BaseScenario {
  created_at: Date | string
  description?: string
  graph: ScenarioGraph
  guid: string
  name: string
  pass_criteria: ScenarioPassCriteria
  updated_at: Date | string
}

export enum ScenarioState {
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
  Archived = 'ARCHIVED'
}

export type ScenarioStep = {
  attacks?: ReturnType<typeof graphNodeToStepAttack>[]
  delay?: number
  id?: string
}

export interface Scenario extends BaseScenario {
  created_by: string
  created_from_type?: TargetScopeReferenceScenarioTypes
  created_from_id?: string
  create_source: string
  edit_counter?: number
  hypothesis?: string
  is_eligible_for_service_creation: boolean
  last_run_at: Date | string
  multi_select_tags?: MultiSelect
  multi_select_labels?: MultiSelect
  org_id: string
  recommended_scenario_id?: string
  state: ScenarioState
  steps?: ScenarioStep[]
}

export interface RecommendedScenario extends BaseScenario {
  hypothesis: string
  multi_select_tags: MultiSelect
  priority: number
  static_endpoint_name: string
}

export interface SharedScenario extends CamelCasedProperties<BaseScenario> {
  hypothesis: string
  multiSelectTags: MultiSelect
  recommendedScenarioId: string
  sharedBy: string
  sharedByTeam: string
  sharedFromScenario: string
}

export type ScenarioSource =
  | Scenario
  | ScenarioRun
  | SharedScenario
  | RecommendedScenario

export interface ServiceTarget {
  serviceId?: string
  targetingStrategy: TargetingStrategy
}

export type PagedScenariosResponse = PagedResponse<Scenario>
